import React, { useState, useEffect, useCallback } from 'react';
import { ReportSelect } from '../ReportSelect';
import DashboardReportService from '@services/DashboardReportService';

import '../ReportViewer.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Layer {
  LayerId: number;
  Name: string;
}

interface DateField {
  CustomFieldId: number;
  Name: string;
}

interface CardType {
  ConfigValueID: number;
  Name: string;
}

interface ApiConfig {
  apiUrl: string;
  authToken: string;
  timeZone: string;
}

interface Dashboard {
  DashboardId: number;
  ReportId: number;
  ConfigValueId?: number;
  ProjectId?: number;
}

interface SaveRequest {
  ReportId: number;
  WidgetId: number;
  DashboardId: number;
  LayerId: number;
  DateFieldId: number;
  AddDynamicGroup: boolean;
  CardType: string;
  ConfigValueID: number;
}

interface Props {
  dashboard: Dashboard;
  config?: ApiConfig;
  settings: Record<string, unknown>;
  widgetId: number;
  onCancel: () => void;
  onSave: (request: SaveRequest, selectedLayer: number, request2: SaveRequest) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ALL_LAYERS: Layer = { LayerId: -1, Name: 'All Layers' };
const ALL_CARD_TYPES: CardType = { ConfigValueID: 0, Name: 'All Card Types' };

// ─── Component ────────────────────────────────────────────────────────────────

const StatisticsSettings: React.FC<Props> = ({
  dashboard,
  config: _config,
  settings,
  widgetId,
  onCancel,
  onSave,
}) =>
{
  // ── State ──────────────────────────────────────────────────────────────────
  const [layers, setLayers] = useState<Layer[]>([{ LayerId: 0, Name: 'Loading...' }]);
  const [dateFields, setDateFields] = useState<DateField[]>([
    { CustomFieldId: 0, Name: 'Loading...' },
  ]);
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);

  const [selectedLayer, setSelectedLayer] = useState<number>(
    settings.LayerId ? parseInt(String(settings.LayerId)) : 0,
  );
  const [selectedDate, setSelectedDate] = useState<number>(
    settings.DateFieldId ? parseInt(String(settings.DateFieldId)) : 0,
  );
  const [addDynamicGroup, setAddDynamicGroup] = useState<boolean>(
    settings.AddDynamicGroup !== undefined ? JSON.parse(String(settings.AddDynamicGroup)) : true,
  );
  const [selectedCardType, setSelectedCardType] = useState<CardType>({
    ConfigValueID: settings.ConfigValueID ? parseInt(String(settings.ConfigValueID)) : 0,
    Name: settings.CardType ? String(settings.CardType) : 'All Card Types',
  });

  const [saving, setSaving] = useState(false);

  // ── Load layers on mount ───────────────────────────────────────────────────
  useEffect(() =>
  {
    DashboardReportService.getPersonLayers().then((data: Layer[]) =>
    {
      setLayers([ALL_LAYERS, ...data]);
    });
  }, []);

  // ── Load date fields + card types ─────────────────────────────────────────
  const loadDataFields = useCallback(
    (layerOverride?: number) =>
    {
      const layerSelectedFilter = layerOverride !== undefined ? layerOverride : selectedLayer;

      if (layerSelectedFilter <= 0 && layerSelectedFilter !== -1) return;

      if (layerOverride !== undefined)
      {
        setDateFields([{ CustomFieldId: 0, Name: 'Loading...' }]);
      }

      const requestCustomFields = {
        LayerId: layerSelectedFilter,
        TypeId: 6,
        configValueId: dashboard.ConfigValueId ?? 1,
      };

      setTimeout(() =>
      {
        DashboardReportService.getCustomFieldsByLayerAndId(requestCustomFields)

          .then(((data: DateField[] | DateField[][]) =>
          {
            let dateFields: DateField[] = [];

            if ((dashboard.ConfigValueId ?? 1) === 1)
            {
              // Flatten grouped arrays and take first of each sorted group
              (data as DateField[][]).forEach(arrValues =>
              {
                if (Array.isArray(arrValues))
                {
                  const sorted = [...arrValues].sort((a, b) => a.CustomFieldId - b.CustomFieldId);
                  dateFields.push(sorted[0]!);
                }
              });
            }
            else
            {
              dateFields = data as DateField[];
            }

            // Resolve the best pre-selected start date field
            let resolvedDate = selectedDate;
            const preselected = dateFields.find(f => f.CustomFieldId === resolvedDate);
            if (!preselected)
            {
              const startDateField = dateFields.find(f => f.Name === 'Start date');
              if (startDateField)
              {
                resolvedDate = startDateField.CustomFieldId;
              }
              else if (dateFields.length > 0)
              {
                resolvedDate = dateFields[0]!.CustomFieldId;
              }
            }

            setDateFields(dateFields);
            setSelectedDate(resolvedDate);

            // Load card types in the same callback to avoid extra re-renders
            DashboardReportService.getMenuByConfigValuePerson(dashboard.ProjectId ?? 0)

              .then(((response: { ConfigValueID: number; Text: string }[]) =>
              {
                const filtered = response
                  .filter(x => ![1, 9, 15].includes(x.ConfigValueID))
                  .map(c => ({ ConfigValueID: c.ConfigValueID, Name: c.Text }));
                setCardTypes([ALL_CARD_TYPES, ...filtered]);
              }) as any)
              .catch(console.error);
          }) as any);
      });
    },
    // selectedDate intentionally excluded: we only want the resolved-at-mount value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLayer, dashboard.ConfigValueId, dashboard.ProjectId],
  );

  useEffect(() =>
  {
    loadDataFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleLayerChange = (option: Layer | null) =>
  {
    if (!option) return;
    setSelectedLayer(option.LayerId);
    loadDataFields(option.LayerId);
  };

  const handleDateChange = (option: DateField | null) =>
  {
    setSelectedDate(option ? option.CustomFieldId : 0);
  };

  const handleCardTypeChange = (option: CardType | null) =>
  {
    setSelectedCardType(option ?? ALL_CARD_TYPES);
  };

  const handleSave = async () =>
  {
    setSaving(true);
    const request: SaveRequest = {
      ReportId: dashboard.ReportId,
      WidgetId: widgetId,
      DashboardId: dashboard.DashboardId,
      LayerId: selectedLayer,
      DateFieldId: selectedDate,
      AddDynamicGroup: addDynamicGroup,
      CardType: selectedCardType.Name,
      ConfigValueID: selectedCardType.ConfigValueID,
    };
    await DashboardReportService.updateWidgetStatisticsSettings(request);
    setSaving(false);
    onSave(request, selectedLayer, request);
  };

  // ── Derived select values (react-select needs full objects) ────────────────
  const selectedLayerObj = layers.find(l => l.LayerId === selectedLayer) ?? null;
  const selectedDateObj = dateFields.find(f => f.CustomFieldId === selectedDate) ?? null;
  const selectedCardTypeObj =
    cardTypes.find(c => c.ConfigValueID === selectedCardType.ConfigValueID) ?? selectedCardType;

  // ── Shared react-select styles ─────────────────────────────────────────────
  const selectStyles = {
    container: (b: object) => ({ ...b, width: 150 }),
    control: (b: object) => ({ ...b, minHeight: 26, height: 26 }),
    menu: (b: object) => ({ ...b, width: 150 }),
  };

  const isDark = document.documentElement.classList.contains('dark');

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <h4
        className="widget-setting-heading type-A"
        style={{ color: isDark ? '#a9a093' : 'inherit' }}
      >
        Widget Settings
      </h4>
      <div className="widget-setting-section-statistics">
        {/* Card Types */}
        <div className="widget-setting-rowStatistics">
          <div className="widget-setting-label">
            <span>Card Types:</span>
          </div>
          <div className="widget-setting-value select-widget-date">
            <ReportSelect
              autoFocus={false}
              options={cardTypes}
              onChange={handleCardTypeChange}
              value={selectedCardTypeObj}
              isMulti={false}
              isSearchable
              openMenuOnFocus
              closeMenuOnSelect
              getOptionLabel={o => o.Name}
              getOptionValue={o => String(o.ConfigValueID)}
              styles={selectStyles}
            />
          </div>
        </div>

        {/* Layer */}
        <div className="widget-setting-rowStatistics select-widget-date">
          <div className="widget-setting-label">
            <span>Layer:</span>
          </div>
          <div className="widget-setting-value">
            <ReportSelect
              autoFocus={false}
              options={layers}
              onChange={handleLayerChange}
              value={selectedLayerObj}
              isMulti={false}
              isSearchable
              openMenuOnFocus
              closeMenuOnSelect
              getOptionLabel={o => o.Name}
              getOptionValue={o => String(o.LayerId)}
              styles={selectStyles}
            />
          </div>
        </div>

        {/* Field as Date */}
        <div className="widget-setting-rowStatistics select-widget-date">
          <div className="widget-setting-label">
            <span>Field as Date:</span>
          </div>
          <div className="widget-setting-value">
            <ReportSelect
              autoFocus={false}
              options={dateFields}
              onChange={handleDateChange}
              value={selectedDateObj}
              isMulti={false}
              isSearchable
              openMenuOnFocus
              closeMenuOnSelect
              getOptionLabel={o => o.Name}
              getOptionValue={o => String(o.CustomFieldId)}
              styles={selectStyles}
            />
          </div>
        </div>

        {/* Include Dynamic Column */}
        <div className="widget-setting-rowStatistics">
          <div className="widget-setting-label">
            <label htmlFor={`CheckboxChangeAddDynamicGroup${widgetId}`}>
              Include Dynamic Column
            </label>
          </div>
          <div className="widget-setting-label">
            <span className="custom-checkbox task-card-checkbox">
              <input
                id={`CheckboxChangeAddDynamicGroup${widgetId}`}
                className="taskCardCheckbox"
                type="checkbox"
                checked={addDynamicGroup}
                onChange={() => setAddDynamicGroup(prev => !prev)}
              />
              <label
                htmlFor={`CheckboxChangeAddDynamicGroup${widgetId}`}
                className="conversationCheckbox"
              />
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="widget-setting-actions flex justify-end gap-2">
          <button
            style={{
              fontSize: 12,
              padding: '4px 14px',
              border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
              borderRadius: 4,
              background: isDark ? '#3a3c3c' : '#fff',
              color: isDark ? '#a9a093' : '#555',
              cursor: 'pointer',
            }}
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            style={{
              fontSize: 12,
              padding: '4px 14px',
              border: 'none',
              borderRadius: 4,
              background: '#62accb',
              color: '#fff',
              cursor: 'pointer',
            }}
            onClick={handleSave}
            type="button"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatisticsSettings;
